import { Loader2 } from "lucide-react";
import { useState } from "react";

import { ClientBrandPreview } from "@/components/client-brand-preview";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  type ClientSummary,
  isApiError,
  useClient,
  useResearchClientBrand,
  useUpdateClient,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const THEMES = [
  { value: "system", label: "System" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
] as const;

interface ClientEditDialogProps {
  /** Client to edit, or `null` while no row's pencil affordance is active. */
  clientId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Edits a client's brand — name, primary/accent color, logo, and default
 * theme. Fetches the current values via `useClient`; the form only mounts
 * once they've loaded, so it always starts from fresh server state.
 */
export function ClientEditDialog({ clientId, open, onOpenChange }: ClientEditDialogProps) {
  const { data: client, isLoading } = useClient(clientId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit client</DialogTitle>
          <DialogDescription className="sr-only">
            Edit brand for {client?.name ?? "this client"}
          </DialogDescription>
        </DialogHeader>
        {isLoading && (
          <DialogBody>
            <div className="space-y-4">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          </DialogBody>
        )}
        {client && open && (
          // Remount with fresh initial state whenever the target client
          // changes, so the form fields reflect the new client without a
          // sync effect.
          <ClientEditForm key={client.id} client={client} onCancel={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ClientEditFormProps {
  client: ClientSummary;
  onCancel: () => void;
}

function ClientEditForm({ client, onCancel }: ClientEditFormProps) {
  const updateClient = useUpdateClient();
  const researchBrand = useResearchClientBrand();
  const [name, setName] = useState(client.name);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState(client.primary_color ?? "");
  const [accentColor, setAccentColor] = useState(client.accent_color ?? "");
  const [logoUrl, setLogoUrl] = useState(client.logo_url ?? "");
  const [designDescription, setDesignDescription] = useState(client.design_description ?? "");
  const [theme, setTheme] = useState(client.theme ?? "system");
  const [error, setError] = useState("");
  const [researchError, setResearchError] = useState("");

  const primaryColorValid = primaryColor === "" || HEX_RE.test(primaryColor);
  const accentColorValid = accentColor === "" || HEX_RE.test(accentColor);
  const canSave = !!name.trim() && primaryColorValid && accentColorValid;
  const saving = updateClient.isPending;
  const researching = researchBrand.isPending;
  const canResearch = !!websiteUrl.trim() && !researching && !saving;

  async function handleResearch() {
    if (!canResearch) {
      return;
    }
    setResearchError("");
    try {
      const result = await researchBrand.mutateAsync({ url: websiteUrl.trim() });
      if (!result) {
        return;
      }
      // Only overwrite a field when the AI confidently returned a value —
      // never clobber something the user already typed with a null.
      if (result.primary_color) {
        setPrimaryColor(result.primary_color);
      }
      if (result.accent_color) {
        setAccentColor(result.accent_color);
      }
      if (result.logo_url) {
        setLogoUrl(result.logo_url);
      }
      if (result.design_description) {
        setDesignDescription(result.design_description);
      }
    } catch (err) {
      logger.error("[client-edit] brand research failed:", err);
      if (isApiError(err) && err.status === 503) {
        setResearchError("AI research isn't configured — fill the brand in manually.");
      } else {
        setResearchError("Couldn't research that site — fill the brand in manually.");
      }
    }
  }

  async function handleSave() {
    if (!canSave) {
      return;
    }
    setError("");
    try {
      await updateClient.mutateAsync({
        clientId: client.id,
        name: name.trim(),
        primary_color: primaryColor || null,
        accent_color: accentColor || null,
        logo_url: logoUrl.trim() || null,
        design_description: designDescription.trim() || null,
        theme,
      });
      onCancel();
    } catch (err) {
      logger.error("[client-edit] save failed:", err);
      setError("Failed to update client. Please try again.");
    }
  }

  return (
    <DialogBody>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="client-edit-name">Name</Label>
          <Input
            id="client-edit-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Client name"
            maxLength={100}
            disabled={researching}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="client-edit-website">Website URL</Label>
          <div className="flex gap-2">
            <Input
              id="client-edit-website"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://acme.com"
              disabled={researching}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleResearch();
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              onClick={() => void handleResearch()}
              disabled={!canResearch}
              className="shrink-0"
            >
              {researching && <Loader2 className="mr-2 size-4 animate-spin" />}
              {researching ? "Researching…" : "Auto-fill from website"}
            </Button>
          </div>
          {researchError && <p className="text-destructive text-xs">{researchError}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <ColorField
            id="client-edit-primary-color"
            label="Primary color"
            value={primaryColor}
            onChange={setPrimaryColor}
            valid={primaryColorValid}
            disabled={researching}
          />
          <ColorField
            id="client-edit-accent-color"
            label="Accent color"
            value={accentColor}
            onChange={setAccentColor}
            valid={accentColorValid}
            disabled={researching}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="client-edit-logo">Logo URL</Label>
          <Input
            id="client-edit-logo"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
            maxLength={500}
            disabled={researching}
          />
          {logoUrl.trim() && (
            <div className="border-border bg-muted/30 mt-1 flex items-center rounded-md border p-2">
              <img
                src={logoUrl.trim()}
                alt="Logo preview"
                className="h-8 max-w-32 object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="client-edit-design-description">Design description</Label>
          <Textarea
            id="client-edit-design-description"
            value={designDescription}
            onChange={(e) => setDesignDescription(e.target.value)}
            placeholder="e.g. Clean, minimal, high-contrast — bold sans-serif headings."
            maxLength={2000}
            disabled={researching}
            className="min-h-20"
          />
          <p className="text-muted-foreground text-xs">Used to brand generated projects.</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="client-edit-theme">Theme</Label>
          <Select value={theme} onValueChange={setTheme} disabled={researching}>
            <SelectTrigger id="client-edit-theme" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {THEMES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <ClientBrandPreview
          name={name}
          primaryColor={primaryColor}
          accentColor={accentColor}
          logoUrl={logoUrl}
        />

        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onCancel} disabled={saving || researching}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!canSave || saving || researching}>
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </DialogFooter>
    </DialogBody>
  );
}

interface ColorFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  valid: boolean;
  disabled?: boolean;
}

/** Native color picker paired with a hex text input; the two stay in sync. */
function ColorField({ id, label, value, onChange, valid, disabled }: ColorFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-2">
        {/* The swatch needs an arbitrary runtime hex value, which Tailwind
            utility classes can't express — a native color input is the only
            way to preview + pick it, so this is one of the few legitimate
            uses of the `value` attribute driving a non-text input directly. */}
        <input
          type="color"
          aria-label={`${label} picker`}
          value={valid && value ? value : "#ffffff"}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="border-input size-9 shrink-0 cursor-pointer rounded-md border p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
        />
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#RRGGBB"
          maxLength={7}
          disabled={disabled}
          className={cn("font-mono", !valid && "border-destructive")}
        />
      </div>
      {!valid && <p className="text-destructive text-xs">Must be a hex color like #4F46E5.</p>}
    </div>
  );
}
